// @flow

import * as React from "react"
import Modal from "react-modal"
import {VATTypes, CommissionTypes} from "../../constants"
import {withTranslation} from "react-i18next"
import {connect} from "react-redux"
import {set, cloneDeep} from "lodash"

import PriceInput from "../price-input"
import NumericStepInput from "../numeric-step-input"
import type {
  FreeProduct,
  Product,
  Dispatch,
  ValidationError,
  BabyListItem,
} from "../../types"
import {updateFreeProductFromBabyList} from "../../actions/baby"

type Props = {
  +t: any,
  +babyToken: string,
  +item: BabyListItem,
  +product: Product,
  +dispatch: Dispatch,
  handleCloseModal: () => void,
  handleAfterOpen: () => void,
}

type State = {
  edits: FreeProduct,
  errors: any,
}

const requiredProps = ["name"]
const isRequired = (property) => requiredProps.includes(property)

class FreeProductEditModal extends React.Component<Props, State> {
  handleSave: () => void
  handleInputChange: (SyntheticInputEvent<HTMLTextAreaElement>) => void
  handleInputPriceChange: (string, string) => void
  handleAmountChange: (number) => void

  constructor(props: Props) {
    super(props)

    this.state = {
      edits: {
        name: props.item.name,
        priceNew: props.item.priceNew,
        priceSelling: props.item.priceSelling,
        pricePurchase: props.item.pricePurchase,
        priceExtra: props.item.priceExtra,
        pricePimp: props.item.pricePimp,
        isFirstPickup: props.item.isFirstPickup,
        isPriceTbd: props.item.isPriceTbd,
        amount: props.item.amount,
        isStar: props.item.isStar,
        isPimp: props.item.isPimp,
        isExtra: props.item.isExtra,
        remarks: props.item.remarks,
        // Product
        isNew: props.product.isNew,
        description: props.product.description,
        vatTypeId: props.product.vatTypeId,
        commissionTypeId: props.product.commissionTypeId,
      },
      errors: {},
    }

    this.handleSave = this.handleSave.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleInputPriceChange = this.handleInputPriceChange.bind(this)
    this.handleAmountChange = this.handleAmountChange.bind(this)
  }

  handleInputChange(e: SyntheticInputEvent<HTMLTextAreaElement>) {
    const target = e.target
    const value = target.type === "checkbox" ? target.checked : target.value
    const name = target.name
    this.edit(name, value)
  }

  handleInputPriceChange(name: string, value: string) {
    this.edit(name, value)
  }

  handleAmountChange(amount) {
    this.edit("amount", amount)
  }

  edit(property: string, value: any) {
    const edits = cloneDeep(this.state.edits)
    set(edits, property, value)
    this.setState({edits})
  }

  validate() {
    const {edits} = this.state
    let isValid = true
    if (edits) {
      isValid = !isValid || this.validateProperty("name", edits.name)
      return isValid
    }
    return false
  }

  validateProperty(property: string, value: string) {
    let error = null
    if (value == "" && isRequired(property)) {
      error = {type: "required"}
    }
    if (error) {
      this.addError(property, error)
      return false
    }
    this.clearError(property)
    return true
  }

  handleSave(e: SyntheticEvent<>) {
    e.preventDefault()
    if (this.validate()) {
      const {dispatch, babyToken, item} = this.props
      const {edits} = this.state
      if (edits) {
        dispatch(updateFreeProductFromBabyList(babyToken, item.id, edits))
      }
      this.props.handleCloseModal()
    }
  }

  addError(fieldName: string, error: ValidationError) {
    const {errors} = this.state
    this.setState({errors: {...errors, [fieldName]: error}})
  }

  clearError(fieldName: string) {
    const {errors} = this.state
    const {[fieldName]: value, ...withoutError} = errors // eslint-disable-line no-unused-vars
    this.setState({
      errors: withoutError,
    })
  }

  hasError(fieldName: string): boolean {
    return Object.keys(this.state.errors).includes(fieldName)
  }

  showError(fieldName: string): ?React.Node {
    const error = this.state.errors[fieldName]
    if (error) {
      return <div className="invalid-feedback">{error.type}</div>
    }
    return null
  }

  renderPriceComboField(checkProperty, priceProperty) {
    return (
      <div className="form-group">
        {this.renderField(checkProperty)}
        <div className="mt-2" />
        {this.renderField(
          priceProperty,
          !this.state.edits[checkProperty],
          false
        )}
      </div>
    )
  }

  renderField(property, readOnly = false, hasLabel = true, isRequired = false) {
    const {edits} = this.state
    const {t} = this.props
    let invalidClass = this.hasError(property) ? "is-invalid" : ""
    let label = (
      <label key={`${property}-lbl`} htmlFor={property}>
        {t(`intake.freeProduct.fields.${property}`)}
        {isRequired && "*"}
      </label>
    )
    let field = null
    switch (property) {
      case "priceNew":
      case "priceSelling":
      case "pricePurchase":
      case "priceExtra":
      case "pricePimp":
        field = (
          <PriceInput
            className={"form-control form-control-sm " + invalidClass}
            key={property}
            name={property}
            value={edits[property]}
            readOnly={readOnly}
            onChange={this.handleInputPriceChange}
          />
        )
        break
      case "amount":
        field = (
          <>
            <br key={`${property}-br`} />
            <NumericStepInput
              key={property}
              className={invalidClass}
              name={property}
              min={0}
              id={property}
              value={parseInt(edits[property] || 0)}
              onChange={this.handleAmountChange}
            />
          </>
        )
        break
      case "description":
      case "remarks":
        field = (
          <textarea
            key={property}
            className={"form-control " + invalidClass}
            value={edits[property] || ""}
            onChange={this.handleInputChange}
            name={property}
          />
        )
        break
      case "commissionTypeId":
        field = (
          <select
            className={"form-control form-control-sm " + invalidClass}
            value={edits[property] || ""}
            name={property}
            id={property}
            key={property}
            onChange={this.handleInputChange}
          >
            {Object.keys(CommissionTypes).map((k) => {
              const coType = CommissionTypes[k]
              return (
                <option key={`${property}-opt-${k}`} value={coType.id}>
                  {coType.name} ({parseFloat(coType.amount) * 100}%)
                </option>
              )
            })}
          </select>
        )
        break
      case "vatTypeId":
        field = (
          <select
            className={"form-control form-control-sm " + invalidClass}
            value={edits[property] || ""}
            name={property}
            id={property}
            key={property}
            onChange={this.handleInputChange}
          >
            {Object.keys(VATTypes).map((k) => {
              const vatType = VATTypes[k]
              return (
                <option key={`${property}-opt-${k}`} value={vatType.id}>
                  {vatType.name}
                </option>
              )
            })}
          </select>
        )
        break
      case "isPimp":
      case "isExtra":
      case "isStar":
      case "isNew":
      case "isPriceTbd":
      case "isFirstPickup":
        field = (
          <div
            key={property}
            className={
              "custom-control custom-checkbox custom-control-inline " +
              invalidClass
            }
          >
            <input
              type="checkbox"
              id={property}
              value={true}
              name={property}
              onChange={this.handleInputChange}
              required={false}
              checked={edits[property]}
              className="custom-control-input"
            />
            <label htmlFor={property} className="custom-control-label">
              {t(`intake.freeProduct.fields.${property}`)}
            </label>
          </div>
        )
        label = null
        break
      default:
        field = (
          <input
            className={"form-control " + invalidClass}
            name={property}
            id={property}
            key={property}
            type="text"
            value={edits[property] || ""}
            onChange={this.handleInputChange}
          />
        )
    }
    return (
      <>
        {hasLabel && label}
        {field}
        {this.hasError(property) && this.showError(property)}
      </>
    )
  }

  render() {
    const {t} = this.props
    return (
      <Modal
        className="modal-dialog modal-lg"
        isOpen={true}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        onRequestClose={this.props.handleCloseModal}
        onAfterOpen={this.props.handleAfterOpen}
        contentLabel={t("intake.freeProduct.update.title")}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {t("intake.freeProduct.update.title")}
            </h5>
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label={t("intake.freeProduct.close")}
              onClick={this.props.handleCloseModal}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <div className="modal-body">
            <div className="row">
              <div className="col-md-8">
                <div className="form-group">
                  {this.renderField("name", false, true, true)}
                </div>
                <div className="form-group">
                  {this.renderField("description")}
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      {this.renderField("priceNew")}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      {this.renderField("pricePurchase")}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      {this.renderField("vatTypeId")}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      {this.renderField("commissionTypeId")}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mt-md-5">
                  {this.renderField("isNew")}
                  <br />
                  {this.renderField("isPriceTbd")}
                </div>
              </div>
            </div>

            <button className="btn btn-primary" onClick={this.handleSave}>
              {t("intake.freeProduct.update.update")}
            </button>
          </div>
        </div>
      </Modal>
    )
  }
}

export default withTranslation()(connect()(FreeProductEditModal))
